<script setup lang="ts">
import type { DragEvent } from 'vue'
import { useShoppingLists } from '@/composables/shoppingLists'
import { watch } from 'vue'
import IconPrompt from '@/assets/icons/IconPrompt.vue'
import IconModel from '@/assets/icons/IconModels.vue'

const {
  carts,
  selectedCart,
  selectedCartItems,
  selectCart,
  addToCart,
  deleteItem,
  saveForLater,
  bringBackToCart,
} = useShoppingLists()


// if the carts array ever changes so that our selectedCart name no longer exists,
// pick the first cart in the new list
watch(carts, (newCarts) => {
  if (!newCarts.some(c => c.name === selectedCart) && newCarts.length > 0) {
    selectCart(newCarts[0].name)
  }
})

const emit = defineEmits<{ (e: 'close'): void }>()

function handleDrop(e: DragEvent) {
  e.preventDefault()

  // get the dragged item payload
  const raw = e.dataTransfer?.getData('application/json')
  if (!raw) return

  let item: any
  try {
    item = JSON.parse(raw)
  } catch (err) {
    console.error('Drop data not valid JSON:', err)
    return
  }

  // switch to the Shopping Bag and add the item
  selectCart('Bag')
  addToCart(item)
}

// optional: prevent default to allow drop
function handleDragOver(e: DragEvent) {
  e.preventDefault()
}
</script>

<template>
  <div>
    <nav class="flex flex-nowrap items-center justify-center gap-1 mt-2">
      <div class="relative group">
        <button
          type="button"
          @click="emit('close')"
          class="text-sm text-[#00ccaa] hover:text-[#00eedd] hidden lg:flex items-center cursor-pointer lg:mt-2"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g
      clip-path="url(#clip0_1477_25604)"
      data-v-inspector="src/components/icons/IconRanaCross.vue:3:5"
    >
      <path
        d="M6.01041 18.0104L18.0312 5.98959M18.0312 18.0104L6.01041 5.98959"
        stroke="#00ccaa"
        stroke-width="3"
        stroke-linecap="round"
        stroke-linejoin="round"
        data-v-inspector="src/components/icons/IconRanaCross.vue:4:7"
      ></path>
    </g>
    <defs data-v-inspector="src/components/icons/IconRanaCross.vue:12:5">
      <clipPath
        id="clip0_1477_25604"
        data-v-inspector="src/components/icons/IconRanaCross.vue:13:7"
      >
        <rect
          width="24"
          height="24"
          fill="white"
          data-v-inspector="src/components/icons/IconRanaCross.vue:14:9"
        ></rect>
      </clipPath>
    </defs>
  </svg>
        </button>
        
      </div>
      <div
        v-for="(cart, index) in carts"
        :key="cart.name"
        class="relative inline-flex min-h-7 cursor-pointer select-none border-2 border-secondary-accent transition-all duration-200 ease-in-out px-2 pt-0.5 mt-4 lg:mt-2"
        :class="[
          selectedCart === cart.name ? 'bg-secondary-accent pl-4 pr-4 text-neutral' : 'border-secondary-accent text-secondary-accent',
          index === 0 ? 'rounded-l-md' : '',
          index === carts.length - 1 ? 'rounded-r-md' : ''
        ]"
        tabindex="0"
        @click="selectCart(cart.name)"
        @keyup.enter="selectCart(cart.name)"
      >
        <span class="text-sm whitespace-nowrap">{{ cart.name }}</span>
        <div
          class="absolute bottom-[-6px] left-1 right-0 mx-auto mt-1.5 h-[2px] w-[85%] rounded-lg bg-secondary-accent"
          :class="selectedCart === cart.name ? 'block' : 'hidden'"
        ></div>
      </div>
    </nav>

    <br /><br />

    <div v-if="selectedCartItems.length > 0">
      <h2 class="ml-2 whitespace-nowrap">{{ selectedCart }} Items</h2>
      <div class="space-y-4 mt-2">
        <div
          v-for="item in selectedCartItems"
          :key="item.id"
          class="w-full bg-gradient-to-r from-[#061a25] to-[#05141d] border border-[#00ccaa33] hover:border-[#00ccaa66] rounded-lg overflow-hidden shadow-lg transition-all duration-300 hover:shadow-[0_0_15px_rgba(0,204,170,0.15)] relative group p-2"
        >
          <div
            class="absolute left-0 top-0 bottom-0 w-1 bg-[#00ccaa] opacity-60 shadow-[0_0_8px_rgba(0,204,170,0.8)]"
          ></div>
          <!-- bag item -->
          <h3 class="font-bold ml-2 mb-2 text-md truncate flex items-start justify-between">
            <span class="flex items-center gap-2">
              <IconPrompt
                v-if="item.tags && item.tags.includes('Prompt')"
                class="w-4 h-4 text-[#00ccaa] flex-shrink-0"
              />
              <IconModel
                v-if="item.tags && item.tags.includes('Models')"
                class="w-4 h-4 text-[#00ccaa] flex-shrink-0"
              />
              <span v-if="(item.qty || 1) > 1">{{ item.qty || 1 }} x </span>
              <div class="relative group">
                <span>
                  {{ item.name.length > 21 ? item.name.substring(0, 18) + '...' : item.name }}
                </span>
                <div 
                  v-if="item.name.length > 21"
                  class="absolute left-0 top-full mt-2 hidden
                         group-hover:block
                         bg-[hsl(var(--accent))] text-neutral text-xs
                         rounded-md px-2 py-1 whitespace-nowrap
                         z-10 max-w-xs">
                  {{ item.name }}
                </div>
              </div>
            </span>
            <button
              class="flex items-center justify-center w-5 h-5 text-xs font-medium bg-[#150a0e] hover:bg-[#280d15] border border-[#ff333350] rounded text-red-400 hover:text-red-300 transition-all duration-200 flex-shrink-0 ml-2"
              @click="deleteItem(item.id)"
            >
              X
            </button>
          </h3>
          <div class="flex justify-end gap-2">
            <button
              v-if="selectedCart === 'Bag'"
              class="flex items-center px-3 py-1.5 text-sm font-medium whitespace-nowrap bg-[#0a1a25] hover:bg-[#0e2434] border border-[#00ccaa50] rounded-md text-[#00ccaa] hover:text-[#00eedd] transition-all duration-200"
              @click="saveForLater(item.id)"
            >
              <!-- Save icon -->
              Save for Later
            </button>
            <button
              v-if="selectedCart === 'Save for Later'"
              class="flex items-center px-3 py-1.5 text-sm font-medium whitespace-nowrap bg-[#0a1a25] hover:bg-[#0e2434] border border-[#00ccaa50] rounded-md text-[#00ccaa] hover:text-[#00eedd] transition-all duration-200"
              @click="bringBackToCart(item.id)"
            >
              <!-- Bring icon -->
              Bring to Cart
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-else>
      <div class="flex flex-col items-center justify-center py-12 text-gray-400 bg-[#05141d20] rounded-lg border border-[#00ccaa20]">
        <!-- Empty state icon -->
        <p class="text-base">No items in this cart</p>
        <p class="text-sm mt-1 text-gray-500">Items you add will appear here</p>
      </div>
    </div>
  </div>
</template>
